import {
  ContentCopy,
  ExpandLessOutlined,
  ExpandMoreOutlined,
  OpenInNew,
} from '@mui/icons-material';
import { Alert, ListItemIcon } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from '@mui/material/Snackbar';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/auth-context';
import { usePermissions } from '../../hooks/use-permissions';
import { AWS_GROUP } from '../../types';
import { shareContent } from '../../utils/share-content';
import { IConnectSignIn } from './integrity-connect-sign-in';
import { NavDataLink } from './nav-data';
import { navRowSx } from './nav-row';

// Material-UI needs to be able to Forward the Ref to the List Items when they are located in another file
// https://material-ui.com/guides/composition/#caveat-with-refs
export const NavSectionLink = React.forwardRef(
  (
    props: { item: NavDataLink; depth?: number },
    ref: React.Ref<any> | undefined,
  ) => {
    // Props
    const { item } = props;
    const depth = props.depth ?? 1;
    // Context
    const { state } = React.useContext(AuthContext);
    // State
    const [show, setShow] = React.useState<boolean>(false);
    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    // Hooks
    const { checkPermissions, checkBillingAccess, hasGroup } = usePermissions();
    const location = useLocation();
    const isActive = location.pathname === item.to;
    const holdsActive = !!item.children?.some(
      (child) => child.to === location.pathname,
    );

    React.useEffect(() => {
      // Open the group that holds the current page, so its row shows
      if (holdsActive) {
        setShow(true);
      }
    }, [holdsActive]);

    let canView = true;
    if (item.hasGroup && item.hasGroup === AWS_GROUP.ARC_BillingSystem) {
      // Check that the Billing User has the necessary group and some permissions have been set
      const hasPermissions: boolean = Boolean(state.billingPermissions.length);
      canView = hasGroup(item.hasGroup) && hasPermissions;
    } else if (item.hasGroup) {
      // User must have this specific group to view link
      canView = hasGroup(item.hasGroup);
    } else if (item.permissions) {
      // User must have one of the permissions
      canView = checkPermissions(item.permissions);
    }

    let hideFromBilling = false;
    if (item.name && item.access) {
      // Only Billing Navigation items with name and access will be hidden and checked
      hideFromBilling = !checkBillingAccess(item.name, item.access);
    }

    let hideFromStaff = false;
    // Check if user is an agent staff account
    const staffAgtNo = state.session?.getAccessToken().decodePayload()[
      'myStaffForAgt'
    ];

    // Check which pages are permitted for agent staff
    const staffPerms =
      state.session?.getAccessToken().decodePayload()['cognito:groups'] || [];
    if (staffAgtNo && item.staffPermission) {
      hideFromStaff = !staffPerms.includes(item.staffPermission);
    }

    if (!hideFromBilling && item.children && canView && !hideFromStaff) {
      return (
        <React.Fragment>
          <ListItem disablePadding>
            <ListItemButton
              aria-expanded={show}
              onClick={() => setShow((current) => !current)}
              sx={navRowSx({ depth })}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {item.startIcon}
              </ListItemIcon>
              <ListItemText primary={item.title} disableTypography />
              {show ? (
                <ExpandLessOutlined sx={{ fontSize: 20, color: '#00000099' }} />
              ) : (
                <ExpandMoreOutlined sx={{ fontSize: 20, color: '#00000099' }} />
              )}
            </ListItemButton>
          </ListItem>

          <Collapse in={show} timeout="auto" unmountOnExit>
            <List dense component="div" disablePadding>
              {item.children.map((link) => {
                const hasNPN = state.status?.NPNVerified;

                if (
                  item.title === 'Integrity Connect' &&
                  link.title === 'Register' &&
                  hasNPN
                ) {
                  return (
                    <IConnectSignIn
                      key="integrity-connect-sign-in"
                      link={link.href}
                    />
                  );
                } else {
                  return (
                    <NavSectionLink
                      key={link.title}
                      item={link}
                      depth={depth + 1}
                    />
                  );
                }
              })}
            </List>
          </Collapse>
        </React.Fragment>
      );
    } else if (!hideFromBilling && item.to && canView && !hideFromStaff) {
      return (
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to={item.to}
            aria-current={isActive ? 'page' : undefined}
            sx={navRowSx({ active: isActive, depth })}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>{item.startIcon}</ListItemIcon>
            <ListItemText primary={item.title} disableTypography />
          </ListItemButton>
        </ListItem>
      );
    } else if (!hideFromBilling && item.href && canView && !hideFromStaff) {
      return (
        <ListItem disablePadding>
          <ListItemButton
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            sx={navRowSx({ depth })}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>{item.startIcon}</ListItemIcon>
            <ListItemText primary={item.title} disableTypography />
            <OpenInNew sx={{ fontSize: 16, color: '#00000066' }} />
          </ListItemButton>
        </ListItem>
      );
    } else if (item.shareLink) {
      const { shareLink } = item;
      let url = shareLink.url;

      if (shareLink.title === 'Registration Link') {
        const RecAgtNo = staffAgtNo || state.user.username;
        url = shareLink?.url + RecAgtNo;
      }

      return (
        <ListItem disablePadding>
          <ListItemButton
            onClick={() =>
              item.shareLink
                ? shareContent({
                    content: { ...item.shareLink, url },
                  })
                : undefined
            }
            sx={navRowSx({ active: isActive, depth })}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {item.startIcon || null}
            </ListItemIcon>
            <ListItemText primary={item.title} disableTypography />
          </ListItemButton>
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={openSnackbar}
            autoHideDuration={3000}
            onClose={() => setOpenSnackbar(false)}
          >
            <Alert
              icon={<ContentCopy fontSize="small" />}
              variant="filled"
              severity="success"
              sx={{ width: '100%', color: 'white' }}
            >
              Link Copied to Clipboard!
            </Alert>
          </Snackbar>
        </ListItem>
      );
    } else {
      return null;
    }
  },
);
